import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  PencilSimple, X, Plus, FloppyDisk, WhatsappLogo, ArrowsClockwise, UsersFour,
} from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const CREWS = ["A", "B", "C", "N/A"];
const OUT = "__OUT__"; // exclusión del turno (misma marca que el backend)

const iso = (d) => new Date(d).toISOString().slice(0, 10);

/**
 * PuebleRosterEditor — permite al supervisor mover operadores entre grupos
 * y cuadrillas antes del broadcast diario del Pueble, agregar operadores no
 * asignados por auto-match y disparar un rebroadcast por WhatsApp.
 */
export default function PuebleRosterEditor() {
  const [open, setOpen] = useState(false);
  const [shiftDate, setShiftDate] = useState(iso(new Date()));
  const [shift, setShift] = useState("dia");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [moves, setMoves] = useState([]);        // [{employee_id, group, role, crew}]
  const [groups, setGroups] = useState({});       // {groupLabel: [roleLabel...]}
  const [rosterPreview, setRosterPreview] = useState(null); // /hr/pueble response
  const [employees, setEmployees] = useState([]); // {id,name,position,crew}
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerEmp, setPickerEmp] = useState("");
  const [pickerGroup, setPickerGroup] = useState("");
  const [pickerRole, setPickerRole] = useState("");
  const [pickerCrew, setPickerCrew] = useState("A");
  const [draggingId, setDraggingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ovr, roster, emps] = await Promise.all([
        api.get("/hr/pueble/roster-override", { params: { date: shiftDate, shift } }),
        api.get("/hr/pueble", { params: { date: shiftDate, shift } }),
        api.get("/hr/employees", { params: { status: "active" } }),
      ]);
      setMoves(ovr.data.moves || []);
      setGroups(ovr.data.groups || {});
      setRosterPreview(roster.data);
      const list = Array.isArray(emps.data) ? emps.data : (emps.data.items || emps.data.employees || []);
      setEmployees(list.filter((e) => e && e.id).map((e) => ({
        id: e.id, name: e.name || "(sin nombre)", position: e.position || "—", crew: e.crew || "N/A",
      })));
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [shiftDate, shift]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const empById = useMemo(() => {
    const m = {};
    employees.forEach((e) => { m[e.id] = e; });
    return m;
  }, [employees]);

  // ---- Matrix (from roster preview, but applying local moves too) ----
  // Build display of the matrix based on preview + local edits: since preview
  // was fetched with the persisted overrides, we need to reflect *local*
  // unsaved changes too.
  const displayMatrix = useMemo(() => {
    if (!rosterPreview) return null;
    // Clone matrix
    const matrix = (rosterPreview.groups || []).map((g) => ({
      group: g.group,
      rows: (g.rows || []).map((r) => ({
        role: r.role, by_crew: { ...(r.by_crew || {}) },
      })),
    }));
    // Remove any names that are moved locally (they'll be re-added below)
    const moveIds = new Set(moves.map((m) => m.employee_id));
    matrix.forEach((g) => g.rows.forEach((r) => {
      Object.keys(r.by_crew).forEach((c) => {
        const lines = (r.by_crew[c] || "").split("\n");
        const kept = lines.filter((line) => {
          const emp = employees.find((e) => e.name === line);
          if (!emp) return true; // keep placeholder / unknown names
          return !moveIds.has(emp.id);
        });
        r.by_crew[c] = kept.length ? kept.join("\n") : r.role;
      });
    }));
    // Add moves as extra lines on the appropriate cells (las exclusiones no se colocan)
    moves.forEach((m) => {
      if (m.group === OUT) return;
      const emp = empById[m.employee_id];
      if (!emp) return;
      let g = matrix.find((x) => x.group === m.group);
      if (!g) { g = { group: m.group, rows: [] }; matrix.push(g); }
      let r = g.rows.find((x) => x.role === m.role);
      if (!r) { r = { role: m.role, by_crew: {} }; g.rows.push(r); }
      const prev = r.by_crew[m.crew] || "";
      const names = prev.split("\n").filter((line) => line && line !== m.role && line !== g.group);
      names.push(emp.name);
      r.by_crew[m.crew] = names.join("\n");
    });
    return matrix;
  }, [rosterPreview, moves, empById, employees]);

  const crewsPresent = useMemo(() => {
    const cs = new Set(rosterPreview?.crews || []);
    moves.forEach((m) => { if (m.group !== OUT) cs.add(m.crew); });
    if (cs.size === 0) return ["A", "B"];
    return Array.from(cs);
  }, [rosterPreview, moves]);

  // --- Add move via picker dialog ---
  const openPicker = (grp = "", role = "", crew = "A") => {
    setPickerEmp("");
    setPickerGroup(grp);
    setPickerRole(role);
    setPickerCrew(crew);
    setPickerOpen(true);
  };
  const confirmPicker = () => {
    if (!pickerEmp || !pickerGroup || !pickerRole) {
      toast.error("Elige colaborador, grupo y rol");
      return;
    }
    setMoves((ms) => {
      const filtered = ms.filter((m) => m.employee_id !== pickerEmp);
      return [...filtered, { employee_id: pickerEmp, group: pickerGroup, role: pickerRole, crew: pickerCrew }];
    });
    setPickerOpen(false);
  };

  const removeMoveByEmp = (empId) => {
    setMoves((ms) => ms.filter((m) => m.employee_id !== empId));
  };
  // Quitar a un auto-asignado del turno (exclusión guardable).
  const excludeEmp = (empId) => {
    setMoves((ms) => [...ms.filter((m) => m.employee_id !== empId), { employee_id: empId, group: OUT, role: OUT, crew: OUT }]);
  };
  const excluded = moves.filter((m) => m.group === OUT);

  // --- Drag & drop between cells ---
  const onDragStart = (empId) => {
    setDraggingId(empId);
  };
  const onDropCell = (grp, role, crew) => {
    if (!draggingId) return;
    setMoves((ms) => {
      const filtered = ms.filter((m) => m.employee_id !== draggingId);
      return [...filtered, { employee_id: draggingId, group: grp, role: role, crew: crew }];
    });
    setDraggingId(null);
  };

  const nameToEmp = (name) => employees.find((e) => e.name === name);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/hr/pueble/roster-override", { date: shiftDate, shift, moves });
      toast.success(`Guardado (${moves.length} override${moves.length === 1 ? "" : "s"})`);
      // Refetch to sync preview with server
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const rebroadcast = async () => {
    if (!window.confirm("¿Enviar Pueble por WhatsApp a todos los operadores ahora?")) return;
    setBroadcasting(true);
    try {
      // Save current moves first so broadcast uses them
      await api.put("/hr/pueble/roster-override", { date: shiftDate, shift, moves });
      const r = await api.post("/hr/pueble/rebroadcast", null, { params: { date: shiftDate, shift } });
      toast.success(`WhatsApp enviado (${r.data.sent || 0} operadores)`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBroadcasting(false); }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="hr-pueble-editor-btn"
                 className="gap-1.5 border-purple-600 text-purple-800 hover:bg-purple-50">
          <PencilSimple size={14} weight="bold" /> Editar Pueble
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-6xl max-h-[92vh] overflow-y-auto"
                     data-testid="hr-pueble-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UsersFour size={18} weight="bold" /> Editor Manual de Pueble (drag & drop)
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div>
            <Label className="overline-label">Fecha del turno</Label>
            <Input type="date" value={shiftDate} onChange={(e) => setShiftDate(e.target.value)}
                    className="h-9" data-testid="hr-pueble-editor-date" />
          </div>
          <div>
            <Label className="overline-label">Turno</Label>
            <Select value={shift} onValueChange={setShift}>
              <SelectTrigger className="h-9" data-testid="hr-pueble-editor-shift"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="dia">Día</SelectItem>
                <SelectItem value="noche">Noche</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button size="sm" variant="outline" onClick={() => openPicker()} className="gap-1"
                     data-testid="hr-pueble-editor-add">
              <Plus size={13} weight="bold" /> Agregar operador
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500 py-4 text-center">Cargando pueble…</p>
        ) : !displayMatrix ? (
          <p className="text-sm text-slate-500 py-4 text-center">Sin datos del turno.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[900px] w-full text-xs border border-slate-300"
                    data-testid="hr-pueble-editor-matrix">
              <thead className="bg-blue-900 text-white">
                <tr>
                  <th className="border border-blue-800 px-2 py-1.5 text-left w-40">Grupo</th>
                  <th className="border border-blue-800 px-2 py-1.5 text-left w-40">Rol</th>
                  {crewsPresent.map((c) => (
                    <th key={c} className="border border-blue-800 px-2 py-1.5">Cuadrilla {c}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white">
                {displayMatrix.map((g, gi) => (
                  g.rows.map((r, ri) => (
                    <tr key={`${g.group}-${r.role}-${gi}-${ri}`}>
                      {ri === 0 && (
                        <td rowSpan={g.rows.length} className="border border-slate-300 px-2 py-1 bg-slate-50 align-top font-semibold text-slate-900">
                          {g.group}
                        </td>
                      )}
                      <td className="border border-slate-300 px-2 py-1 bg-slate-50 align-top text-slate-700">
                        {r.role}
                      </td>
                      {crewsPresent.map((c) => {
                        const cell = r.by_crew[c] || "";
                        const lines = cell.split("\n").filter((n) => n && n !== r.role && n !== g.group);
                        return (
                          <td key={c}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => onDropCell(g.group, r.role, c)}
                              className="border border-slate-300 px-1.5 py-1 align-top min-w-[110px]"
                              data-testid={`hr-pueble-editor-cell-${gi}-${ri}-${c}`}>
                            {lines.length === 0 && (
                              <span className="text-[10px] italic text-slate-300">— vacío —</span>
                            )}
                            <div className="flex flex-col gap-0.5">
                              {lines.map((name, idx) => {
                                const emp = nameToEmp(name);
                                const isMove = emp && moves.find((m) => m.employee_id === emp.id);
                                return (
                                  <div key={`${name}-${idx}`}
                                        draggable={!!emp}
                                        onDragStart={() => emp && onDragStart(emp.id)}
                                        className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] font-medium cursor-grab active:cursor-grabbing ${
                                          isMove
                                            ? "bg-purple-100 text-purple-900 border border-purple-300"
                                            : "bg-emerald-50 text-emerald-900 border border-emerald-200"
                                        }`}>
                                    {name}
                                    {emp && (
                                      <button type="button" onClick={() => (isMove ? removeMoveByEmp(emp.id) : excludeEmp(emp.id))}
                                              className="rounded-full hover:bg-red-100 p-0.5"
                                              title={isMove ? "Quitar override" : "Quitar del turno"}
                                              data-testid={`hr-pueble-editor-remove-${emp.id}`}>
                                        <X size={9} weight="bold" className="text-red-700" />
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <button type="button" onClick={() => openPicker(g.group, r.role, c)}
                                    className="mt-1 text-[9px] text-blue-700 hover:underline"
                                    data-testid={`hr-pueble-editor-add-cell-${gi}-${ri}-${c}`}>
                              + agregar
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ))}
              </tbody>
            </table>
          </div>
        )}

        {excluded.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px] bg-red-50 border border-red-200 rounded p-2" data-testid="hr-pueble-editor-excluded">
            <span className="font-bold text-red-800 mr-1">Quitados del turno ({excluded.length}):</span>
            {excluded.map((m) => (
              <span key={m.employee_id} className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-white border border-red-300 text-red-900 line-through">
                {empById[m.employee_id]?.name || m.employee_id}
                <button type="button" onClick={() => removeMoveByEmp(m.employee_id)} className="no-underline text-blue-700 hover:underline ml-1" title="Restaurar"
                        data-testid={`hr-pueble-editor-restore-${m.employee_id}`}>restaurar</button>
              </span>
            ))}
          </div>
        )}

        <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500">
          <span className="inline-block w-3 h-3 rounded bg-emerald-100 border border-emerald-300" /> auto-match
          <span className="inline-block w-3 h-3 rounded bg-purple-100 border border-purple-300 ml-3" /> override manual
          <span className="ml-auto italic">Arrastra los chips entre celdas, usa "+ agregar" o ✕ para quitar del turno</span>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 pt-4">
          <div className="text-[11px] text-slate-500 mr-auto">
            {moves.length} override{moves.length === 1 ? "" : "s"} pendiente{moves.length === 1 ? "" : "s"} de guardar
          </div>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="hr-pueble-editor-close">
            Cerrar
          </Button>
          <Button onClick={save} disabled={saving} className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                   data-testid="hr-pueble-editor-save">
            <FloppyDisk size={14} weight="bold" /> {saving ? "Guardando…" : "Guardar overrides"}
          </Button>
          <Button onClick={rebroadcast} disabled={broadcasting}
                   className="bg-emerald-700 hover:bg-emerald-800 text-white gap-1.5"
                   data-testid="hr-pueble-editor-rebroadcast">
            <WhatsappLogo size={14} weight="fill" />
            {broadcasting ? "Enviando…" : "Rebroadcast WhatsApp"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Picker to add / assign an employee */}
    <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
      <DialogContent className="bg-white max-w-md" data-testid="hr-pueble-editor-picker">
        <DialogHeader>
          <DialogTitle className="font-heading">Asignar operador</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="overline-label">Colaborador</Label>
            <Select value={pickerEmp} onValueChange={setPickerEmp}>
              <SelectTrigger className="h-9" data-testid="hr-pueble-editor-picker-emp">
                <SelectValue placeholder="Elige…" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-64 overflow-y-auto">
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name} · {e.position} · Cuadr. {e.crew}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="overline-label">Grupo</Label>
              <Select value={pickerGroup} onValueChange={(v) => { setPickerGroup(v); setPickerRole(""); }}>
                <SelectTrigger className="h-9" data-testid="hr-pueble-editor-picker-group">
                  <SelectValue placeholder="Elige…" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-64 overflow-y-auto">
                  {Object.keys(groups).map((g) => (<SelectItem key={g} value={g}>{g}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline-label">Rol</Label>
              <Select value={pickerRole} onValueChange={setPickerRole} disabled={!pickerGroup}>
                <SelectTrigger className="h-9" data-testid="hr-pueble-editor-picker-role">
                  <SelectValue placeholder="Elige…" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {(groups[pickerGroup] || []).map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="overline-label">Cuadrilla</Label>
            <Select value={pickerCrew} onValueChange={setPickerCrew}>
              <SelectTrigger className="h-9" data-testid="hr-pueble-editor-picker-crew"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {CREWS.map((c) => (<SelectItem key={c} value={c}>Cuadrilla {c}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setPickerOpen(false)}>Cancelar</Button>
          <Button onClick={confirmPicker} className="bg-purple-700 hover:bg-purple-800 gap-1.5"
                   data-testid="hr-pueble-editor-picker-confirm">
            <ArrowsClockwise size={14} weight="bold" /> Asignar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
